import { useEffect, useRef, useState } from 'react';

export default function WhyWePayMoreSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const steps = [
    {
      number: '01',
      title: '店舗コストを、そのまま買取価格へ',
      subtitle: 'No Physical Store Costs',
      description: '私たちは実店舗を構えず、出張買取に特化した運営を行っています。維持費（家賃や光熱費）といった運営コストを極限まで抑えることで、その余剰分をすべてお客様への査定額に上乗せしています。無駄を省いた効率的な体制こそが、確かな高価買取の裏付けです。',
      icon: 'ri-store-3-line',
      image: 'https://public.readdy.ai/ai/img_res/b7d34f429061f2429856a4495df3c881.jpg',
      benefit: '固定費削減分を買取価格に還元',
    },
    {
      number: '02',
      title: 'お客様の手間はゼロ。煩雑な手続きも一括サポート',
      subtitle: 'One-Stop Support',
      description: '相続や実家の整理には、本来多くの書類や手配が伴います。買い取りかわせみでは、査定から運び出し、さらには提携先と連携した専門的なご相談までワンストップで対応いたします。お客様が複数の業者とやり取りしたり、複雑な書類に悩まされることはありません。貴重な時間を、ご家族との大切な時間のために使っていただきたい。それが私たちの願いです。',
      icon: 'ri-shake-hands-line',
      image: 'https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/881a3c5fe3a648c575447dcefcb5396e.jpeg',
      benefit: '「丸投げ」で完結。お客様の負担を最小限に。',
    },
    {
      number: '03',
      title: '決断を止めない、圧倒的なスピード解決',
      subtitle: 'Immediate Settlement',
      description: '「今すぐスッキリさせたい」という想いに応えるため、柔軟なスケジュール調整と迅速な訪問を徹底しています。熟練の鑑定士がその場で一点一点スピーディーに査定し、金額にご納得いただければ、その場で現金にてお支払いいたします。銀行振込を待つ時間や、オークションの結果を待つ不安とは無縁の、誠実で素早い解決をお約束します。',
      icon: 'ri-timer-flash-line',
      image: 'https://static.readdy.ai/image/aec91cb01fdf30f00658c017aec0d8c9/b4179ec46fd749f4d1ba33293d75eb00.png',
      benefit: 'お待たせしません。スピード訪問と即現金化を実現。',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-32 px-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #fdfbf7 0%, #f5f1e8 100%)',
      }}
    >
      {/* 和紙テクスチャ背景 */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* ゴールドアクセントライン */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-60"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* セクションヘッダー */}
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="text-[#00A58E] font-bold text-sm tracking-widest uppercase border-b-2 border-[#00A58E] pb-1">
              Why We Pay More
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-8" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            買い取りかわせみが『高価買取』をお約束できる理由
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            大手には真似できない、私たちが査定額にこだわるための『3つの仕組み』をご紹介します。
          </p>
        </div>

        {/* 3ステップフロー */}
        <div className="space-y-16 mb-20">
          {steps.map((step, index) => (
            <div key={index}>
              {/* ステップカード */}
              <div
                className={`bg-white rounded-3xl overflow-hidden shadow-2xl transition-all duration-700 transform ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{
                  transitionDelay: `${index * 200}ms`,
                }}
              >
                <div className="grid md:grid-cols-2 gap-0">
                  {/* 画像部分 */}
                  <div className={`relative h-96 w-full overflow-hidden ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700"
                    />
                    {/* ナンバーバッジ */}
                    <div className="absolute top-6 left-6 w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full shadow-2xl">
                      <span className="text-3xl font-bold text-gray-800" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* コンテンツ部分 */}
                  <div className={`p-12 md:p-16 flex flex-col justify-center ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                    {/* アイコン */}
                    <div className="w-20 h-20 flex items-center justify-center bg-[#00A58E]/10 rounded-full mb-8">
                      <i className={`${step.icon} text-5xl text-[#00A58E] w-12 h-12 flex items-center justify-center`}></i>
                    </div>

                    {/* タイトル */}
                    <h3 className="text-4xl md:text-5xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#00A58E] font-semibold tracking-wider uppercase mb-6">
                      {step.subtitle}
                    </p>

                    {/* 説明文 */}
                    <p className="text-lg text-gray-600 leading-relaxed mb-8">
                      {step.description}
                    </p>

                    {/* ベネフィットバッジ */}
                    <div className="inline-flex items-center gap-3 bg-gradient-to-r from-[#00A58E]/10 to-teal-50 border-l-4 border-[#00A58E] px-6 py-4 rounded-r-xl">
                      <i className="ri-check-double-line text-[#00A58E] text-2xl w-6 h-6 flex items-center justify-center"></i>
                      <span className="text-base font-bold text-gray-800">
                        {step.benefit}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 矢印（最後のステップ以外） */}
              {index < steps.length - 1 && (
                <div className="flex justify-center my-8">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#00A58E] to-teal-600 rounded-full shadow-lg">
                    <i className="ri-arrow-down-line text-3xl text-white w-8 h-8 flex items-center justify-center"></i>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 結論セクション */}
        <div className="text-center">
          <div className="bg-white rounded-3xl border-2 border-[#D4AF37]/30 px-12 py-16 md:px-16 md:py-20 max-w-5xl mx-auto" style={{ boxShadow: '0 8px 48px 0 rgba(0,165,142,0.08), 0 2px 16px 0 rgba(212,175,55,0.10)' }}>
            {/* メダルアイコン */}
            <div className="flex items-center justify-center mb-8">
              <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full">
                <i className="ri-medal-line text-white text-5xl w-12 h-12 flex items-center justify-center"></i>
              </div>
            </div>

            {/* メインタイトル */}
            <h3 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              買い取りかわせみが、<br />最高値の還元を追求し続けられる理由
            </h3>

            {/* ゴールドライン */}
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent mx-auto mb-8"></div>

            {/* リード文 */}
            <p className="text-lg text-gray-600 leading-relaxed mb-14 max-w-2xl mx-auto" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              「適正な評価」を、「最大限の還元」へ。<br />
              私たちは独自の運営体制と、専門的な鑑定眼を掛け合わせることで、お客様の大切なお品物が持つ「真の価値」を逃さず、誠実な価格で評価いたします。
            </p>

            {/* 3つの数値実績ボックス */}
            <div className="grid md:grid-cols-3 gap-6 mb-14">
              {/* 左：還元率 */}
              <div className="bg-gradient-to-b from-[#00A58E]/5 to-teal-50/60 rounded-2xl border border-[#00A58E]/20 px-8 py-10 flex flex-col items-center text-center">
                <div className="text-6xl font-bold text-[#00A58E] mb-3 leading-none" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  30<span className="text-3xl">%</span>
                </div>
                <p className="text-sm font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  最大30%以上の還元率向上
                </p>
                <div className="w-10 h-px bg-[#D4AF37]/60 mb-3"></div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  実店舗を持たない出張特化型の運営により、固定費を徹底的に削減。その余剰分をすべて査定額に充当しています。
                </p>
              </div>

              {/* 中央：専門市場 */}
              <div className="bg-gradient-to-b from-[#D4AF37]/8 to-amber-50/60 rounded-2xl border border-[#D4AF37]/25 px-8 py-10 flex flex-col items-center text-center relative">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-[#D4AF37] text-white text-xs font-bold px-4 py-1 rounded-full tracking-wider whitespace-nowrap">GLOBAL</span>
                </div>
                <div className="text-6xl font-bold text-[#D4AF37] mb-3 leading-none mt-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  15<span className="text-3xl">+</span>
                </div>
                <p className="text-sm font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  国内外15以上の専門市場と提携
                </p>
                <div className="w-10 h-px bg-[#D4AF37]/60 mb-3"></div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  日本・香港・シンガポールなど、世界中のオークションや専門市場から、その時々の最高値を見極めます。
                </p>
              </div>

              {/* 右：鑑定実績 */}
              <div className="bg-gradient-to-b from-[#00A58E]/5 to-teal-50/60 rounded-2xl border border-[#00A58E]/20 px-8 py-10 flex flex-col items-center text-center">
                <div className="text-6xl font-bold text-[#00A58E] mb-3 leading-none" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  10<span className="text-3xl">年</span>
                </div>
                <p className="text-sm font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  古物商歴10年の確かな鑑定実績
                </p>
                <div className="w-10 h-px bg-[#D4AF37]/60 mb-3"></div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  累計数万点の品々を拝見してきた鑑定士・吉田が、一点一点に込められた価値を厳正に判断いたします。
                </p>
              </div>
            </div>

            {/* クロージングメッセージ */}
            <div className="bg-gradient-to-r from-[#00A58E]/8 to-teal-50/80 border border-[#00A58E]/25 rounded-2xl px-8 py-7">
              <p className="text-base text-gray-700 leading-relaxed" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                <strong className="text-[#00A58E] text-lg">まずは、大切なお品物の「今の価値」を確かめてみませんか？</strong><br />
                <span className="text-sm mt-2 inline-block">他社様との比較も歓迎いたします。私たちは、その違いに自信を持っています。</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ゴールドアクセントライン */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-60"></div>
    </section>
  );
}